import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ColorPicker from '../../../components/ColorPicker';
import SkinCanvas, { convertOffsets, isHeartAsset, DEFAULT_LUV_COLOUR } from '../../../components/staff/skins/SkinCanvas';
import { resolveSkinAssetUrl } from '../../../data/profileSkins';
import StaffNotFound from '../StaffNotFound';
import {
    getAdminProfileSkins,
    createProfileSkin,
    updateProfileSkin,
    deleteProfileSkin,
    uploadSkinAsset
} from '../../../services/api';

const PLACEMENTS = [
    { value: 'header', label: 'Header' },
    { value: 'body', label: 'Body' },
    { value: 'heart', label: 'Luv heart' }
];

const ANCHORS = [
    { value: 'top-left', label: 'Top left' },
    { value: 'top-center', label: 'Top centre' },
    { value: 'top-right', label: 'Top right' },
    { value: 'bottom-left', label: 'Bottom left' },
    { value: 'bottom-center', label: 'Bottom centre' },
    { value: 'bottom-right', label: 'Bottom right' }
];

const MAX_RADIUS = 20;

const EMPTY_SKIN = {
    skinId: '',
    name: '',
    backgroundImage: '',
    backgroundMode: '',
    borderInner: '',
    borderMiddle: '',
    borderOuter: '',
    borderGradient: '',
    borderAnimGradient: '',
    borderRadius: '0px',
    verticalOffset: 0,
    staffOnly: false,
    presetBackgrounds: [],
    assets: [],
    sortOrder: 0,
    isActive: true
};

const input = 'w-full border border-gray-300 bg-white px-2 py-[5px] text-[13px] text-gray-800 outline-none focus:border-maroon';
const label = "block font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-maroon";
const btn = "font-['Jersey_10'] uppercase text-[15px] tracking-[1px] px-3 py-2 rounded transition-all";
const shadow = { boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.2)' };

const radiusToNumber = (value) => {
    const n = parseInt(String(value ?? '').replace(/[^0-9-]/g, ''), 10);
    if (!Number.isFinite(n)) return 0;
    return Math.min(MAX_RADIUS, Math.max(0, n));
};

function Panel({ title, children, right }) {
    return (
        <section className="border border-gray-300 bg-offwhite" style={{ boxShadow: '0 3px 0 0 rgba(0,0,0,0.5)' }}>
            <header className="flex items-center justify-between gap-2 bg-maroon px-3 py-[6px]">
                <h3 className="font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-white">{title}</h3>
                {right}
            </header>
            <div className="p-3">{children}</div>
        </section>
    );
}

function Swatch({ value, onChange, title }) {
    return (
        <div className="flex items-center gap-2">
            <ColorPicker
                value={value || '#ffffff'}
                onChange={(e) => onChange(e.target.value)}
                title={title}
                className="h-[28px] w-[38px] shrink-0"
            />
            <input className={input} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder="none" />
            {value && (
                <button type="button" onClick={() => onChange('')} className="shrink-0 text-[12px] text-gray-500 hover:text-maroon">
                    clear
                </button>
            )}
        </div>
    );
}

function ImageSlot({ value, onChange, onPick, label: slotLabel, uploading }) {
    return (
        <div className="flex items-center gap-2">
            <div
                className="h-[46px] w-[46px] shrink-0 border border-gray-300 bg-[#e5e7eb]"
                style={value ? { backgroundImage: `url(${resolveSkinAssetUrl(value)})`, backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' } : undefined}
            />
            <div className="min-w-0 flex-1">
                <input className={input} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={slotLabel} />
            </div>
            <button
                type="button"
                onClick={onPick}
                disabled={uploading}
                className={`${btn} shrink-0 bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-60`}
                style={shadow}
            >
                {uploading ? '...' : 'Upload'}
            </button>
        </div>
    );
}

function ProfileSkins({ userData }) {
    const [skins, setSkins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [form, setForm] = useState(EMPTY_SKIN);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    const [selectedAsset, setSelectedAsset] = useState(null);
    const [assetSizes, setAssetSizes] = useState({});
    const [boxSize, setBoxSize] = useState({ w: 420, h: 0 });
    const [clip, setClip] = useState(false);
    const [uploadingTo, setUploadingTo] = useState(null);

    const [headerColour, setHeaderColour] = useState('#5A2929');
    const [luvColour, setLuvColour] = useState(DEFAULT_LUV_COLOUR);
    const [wallpaper, setWallpaper] = useState('');

    const fileInputRef = useRef(null);
    const uploadTargetRef = useRef(null);

    const isCreating = selectedId === 'new';

    const load = useCallback(async (keepId = null) => {
        setLoading(true);
        const res = await getAdminProfileSkins().catch(() => null);

        if (res?.success) {
            setSkins(res.skins || []);
            if (keepId) {
                const match = (res.skins || []).find(s => s.id === keepId);
                if (match) {
                    setSelectedId(match.id);
                    setForm({ ...EMPTY_SKIN, ...match });
                    setDirty(false);
                }
            }
        } else {
            toast.error('Could not load profile skins');
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleBoxSize = useCallback((size) => {
        setBoxSize(prev => (prev.w === size.w && prev.h === size.h ? prev : size));
    }, []);

    const set = (patch) => { setForm(prev => ({ ...prev, ...patch })); setDirty(true); };

    const openSkin = (skin) => {
        if (dirty && !window.confirm('You have unsaved changes. Discard them?')) return;
        setSelectedId(skin.id);
        setForm({ ...EMPTY_SKIN, ...skin });
        setSelectedAsset(null);
        setAssetSizes({});
        setWallpaper(skin.presetBackgrounds?.[0]?.image || '');
        setDirty(false);
    };

    const startNew = () => {
        if (dirty && !window.confirm('You have unsaved changes. Discard them?')) return;
        setSelectedId('new');
        setForm({ ...EMPTY_SKIN });
        setSelectedAsset(null);
        setAssetSizes({});
        setWallpaper('');
        setDirty(false);
    };

    // ── Assets ───────────────────────────────────────────────────────────────
    const patchAsset = (index, patch) =>
        set({ assets: (form.assets || []).map((a, i) => (i === index ? { ...a, ...patch } : a)) });

    const moveAsset = (index, offsets) => patchAsset(index, { offsets });

    const changeAnchor = (index, nextAnchor) => {
        const asset = form.assets[index];
        const offsets = convertOffsets(asset.offsets, asset.anchor, nextAnchor, boxSize, assetSizes[index]);
        patchAsset(index, { anchor: nextAnchor, offsets });
    };

    const changePlacement = (index, placement) => {
        const asset = form.assets[index];
        if (placement === 'heart') {
            patchAsset(index, { placement, heartDepth: asset.heartDepth || 'infront' });
        } else {
            patchAsset(index, { placement, anchor: asset.anchor || 'top-left', offsets: asset.offsets || {} });
        }
    };

    const removeAsset = (index) => {
        set({ assets: form.assets.filter((_, i) => i !== index) });
        setSelectedAsset(null);
    };

    const reorderAsset = (index, delta) => {
        const target = index + delta;
        if (target < 0 || target >= form.assets.length) return;
        const next = [...form.assets];
        [next[index], next[target]] = [next[target], next[index]];
        set({ assets: next });
        setSelectedAsset(target);
    };

    // ── Uploads ──────────────────────────────────────────────────────────────
    const pickFile = (target) => {
        uploadTargetRef.current = target;
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
            fileInputRef.current.click();
        }
    };

    const doUpload = async (file, target) => {
        setUploadingTo(target.kind === 'asset' ? `asset-${target.index}` : target.kind);

        const res = await uploadSkinAsset(file);
        setUploadingTo(null);

        if (!res?.success) {
            toast.error(res?.message || 'Upload failed');
            return;
        }

        if (target.kind === 'background') set({ backgroundImage: res.url });
        else if (target.kind === 'preset') {
            set({ presetBackgrounds: (form.presetBackgrounds || []).map((p, i) => (i === target.index ? { image: res.url } : p)) });
            setWallpaper(res.url);
        } else if (target.kind === 'asset') patchAsset(target.index, { image: res.url });
        else if (target.kind === 'newAsset') {
            const next = [...(form.assets || []), { image: res.url, placement: 'header', anchor: 'top-left', offsets: {} }];
            set({ assets: next });
            setSelectedAsset(next.length - 1);
        }

        toast.success('Uploaded');
    };

    const onFileChosen = (e) => {
        const file = e.target.files?.[0];
        const target = uploadTargetRef.current;
        if (file && target) doUpload(file, target);
    };

    // ── Save / delete ────────────────────────────────────────────────────────
    const save = async () => {
        if (!form.name.trim()) return toast.error('Give the skin a name');
        if (isCreating && !/^[A-Za-z0-9_-]{1,50}$/.test(form.skinId.trim())) {
            return toast.error('Skin ID must be letters, numbers, dashes or underscores');
        }

        setSaving(true);

        const payload = {
            ...form,
            skinId: form.skinId.trim(),
            name: form.name.trim(),
            borderRadius: `${radiusToNumber(form.borderRadius)}px`,
            verticalOffset: Number(form.verticalOffset) || 0,
            sortOrder: Number(form.sortOrder) || 0,
            presetBackgrounds: (form.presetBackgrounds || []).filter(p => p.image && p.image.trim()),
            assets: (form.assets || []).filter(a => a.image && a.image.trim())
        };

        const res = isCreating
            ? await createProfileSkin(payload).catch(e => ({ success: false, message: e?.message }))
            : await updateProfileSkin(selectedId, payload).catch(e => ({ success: false, message: e?.message }));

        setSaving(false);

        if (res?.success) {
            toast.success(isCreating ? 'Skin created' : 'Skin saved');
            setDirty(false);
            await load(isCreating ? res.id : selectedId);
        } else {
            toast.error(res?.message || 'Could not save the skin');
        }
    };

    const remove = async () => {
        if (isCreating || !selectedId) return;
        if (!window.confirm(`Delete "${form.name}"? This cannot be undone.`)) return;

        const res = await deleteProfileSkin(selectedId).catch(e => ({ success: false, message: e?.message }));

        if (res?.success) {
            toast.success('Skin deleted');
            setSelectedId(null);
            setForm(EMPTY_SKIN);
            setDirty(false);
            await load();
        } else {
            toast.error(res?.message || 'Could not delete the skin', { autoClose: 7000 });
        }
    };

    if (!userData || !userData.hasAdminRole) return <StaffNotFound inline />;

    const gradientMode = form.borderAnimGradient ? 'animated' : form.borderGradient ? 'static' : 'none';
    const gradientValue = form.borderAnimGradient || form.borderGradient || '';

    const setGradient = (mode, value) => {
        if (mode === 'none') return set({ borderGradient: '', borderAnimGradient: '' });
        if (mode === 'animated') return set({ borderGradient: '', borderAnimGradient: value || 'rainbow' });
        return set({ borderGradient: value || 'rainbow', borderAnimGradient: '' });
    };

    const selected = selectedAsset !== null ? form.assets?.[selectedAsset] : null;
    const selectedIsHeart = selected ? isHeartAsset(selected) : false;

    return (
        <div className="flex flex-col gap-3">
            <input ref={fileInputRef} type="file" accept="image/png,image/gif,image/webp,image/jpeg" className="hidden" onChange={onFileChosen} />

            <Panel
                title="Profile skins"
                right={
                    <button type="button" onClick={startNew} className="font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-gold hover:underline">
                        + New skin
                    </button>
                }
            >
                {loading ? (
                    <p className="text-[13px] text-gray-600">Loading...</p>
                ) : skins.length === 0 ? (
                    <p className="text-[13px] text-gray-600">No skins yet.</p>
                ) : (
                    <div className="flex max-h-[230px] flex-col gap-1 overflow-y-auto pr-1">
                        {skins.map(skin => {
                            const active = selectedId === skin.id;
                            return (
                                <button
                                    key={skin.id}
                                    type="button"
                                    onClick={() => openSkin(skin)}
                                    className={`flex items-center gap-3 rounded px-2 py-[6px] text-left transition-all
                                        ${active ? 'bg-maroon text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                                    style={shadow}
                                >
                                    <span
                                        className="h-[30px] w-[54px] shrink-0 border border-black/20 bg-[#e5e7eb]"
                                        style={skin.backgroundImage ? { backgroundImage: `url(${resolveSkinAssetUrl(skin.backgroundImage)})`, backgroundRepeat: 'repeat', backgroundSize: 'auto' } : undefined}
                                    />
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate font-['Jersey_10'] text-[16px] uppercase tracking-[1px]">{skin.name}</span>
                                        <span className={`block truncate text-[11px] ${active ? 'text-white/70' : 'text-gray-500'}`}>
                                            {skin.skinId} · {skin.owners} {skin.owners === 1 ? 'owner' : 'owners'} · {(skin.assets || []).length} assets
                                        </span>
                                    </span>
                                    <span className="flex shrink-0 items-center gap-2">
                                        {!skin.isActive && <span className="font-['Jersey_10'] text-[12px] uppercase tracking-[1px] text-orange-500">off</span>}
                                        {skin.staffOnly && <span className="font-['Jersey_10'] text-[12px] uppercase tracking-[1px] text-gold">staff</span>}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </Panel>

            {selectedId === null ? (
                <Panel title="Editor">
                    <p className="text-[14px] text-gray-700">
                        Pick a skin above to edit it, or create a new one. Drop an image straight onto the canvas to add it as an asset,
                        then drag it into place.
                    </p>
                </Panel>
            ) : (
                <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
                    {/* Canvas */}
                    <div className="xl:col-span-7">
                        <div className="xl:sticky xl:top-[56px]">
                            <Panel
                                title="Canvas"
                                right={
                                    <div className="flex items-center gap-3">
                                        <label className="flex items-center gap-1 text-[13px] text-white/80">
                                            <input type="checkbox" checked={clip} onChange={(e) => setClip(e.target.checked)} />
                                            Clip to box
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => pickFile({ kind: 'newAsset' })}
                                            className="font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-gold hover:underline"
                                        >
                                            + Add asset
                                        </button>
                                    </div>
                                }
                            >
                                <div className="mb-2 flex flex-wrap items-center gap-3">
                                    <div className="flex items-center gap-1">
                                        <span className="font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-gray-600">Header</span>
                                        <ColorPicker value={headerColour} onChange={(e) => setHeaderColour(e.target.value)} title="Preview header colour" className="h-[24px] w-[32px]" />
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <span className="font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-gray-600">Heart</span>
                                        <ColorPicker value={luvColour} onChange={(e) => setLuvColour(e.target.value)} title="Preview luv colour" className="h-[24px] w-[32px]" />
                                    </div>
                                    <label className="flex items-center gap-1">
                                        <span className="font-['Jersey_10'] text-[13px] uppercase tracking-[1px] text-gray-600">Wallpaper</span>
                                        <select className="border border-gray-300 bg-white px-2 py-[3px] text-[12px]" value={wallpaper} onChange={(e) => setWallpaper(e.target.value)}>
                                            <option value="">None</option>
                                            {(form.presetBackgrounds || []).filter(p => p.image).map((p, i) => (
                                                <option key={i} value={p.image}>Preset {i + 1}</option>
                                            ))}
                                        </select>
                                    </label>
                                </div>

                                <SkinCanvas
                                    skin={form}
                                    clip={clip}
                                    selectedIndex={selectedAsset}
                                    onSelect={setSelectedAsset}
                                    onMoveAsset={moveAsset}
                                    onAssetSize={(i, size) => setAssetSizes(prev => ({ ...prev, [i]: size }))}
                                    onBoxSize={handleBoxSize}
                                    onDropFile={(file) => doUpload(file, { kind: 'newAsset' })}
                                    headerColour={headerColour}
                                    luvColour={luvColour}
                                    wallpaper={wallpaper}
                                />

                                <p className="mt-2 text-[12px] text-gray-500">
                                    Header and heart colours are preview-only, each player picks their own. Drag box assets to place them;
                                    heart assets always sit centred on the heart.
                                </p>

                                {selected && (
                                    <div className="mt-3 border border-gray-300 bg-white p-2">
                                        <div className="mb-2 flex items-center justify-between">
                                            <span className={label}>Selected asset #{selectedAsset + 1}</span>
                                            <div className="flex gap-2">
                                                <button type="button" onClick={() => reorderAsset(selectedAsset, -1)} className="text-[12px] text-gray-600 hover:text-maroon">back</button>
                                                <button type="button" onClick={() => reorderAsset(selectedAsset, 1)} className="text-[12px] text-gray-600 hover:text-maroon">forward</button>
                                                <button type="button" onClick={() => removeAsset(selectedAsset)} className="text-[12px] text-red-600 hover:underline">remove</button>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                                            <label className="block">
                                                <span className={label}>Zone</span>
                                                <select className={input} value={selected.placement || 'header'} onChange={(e) => changePlacement(selectedAsset, e.target.value)}>
                                                    {PLACEMENTS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                                </select>
                                            </label>

                                            {selectedIsHeart ? (
                                                <label className="block">
                                                    <span className={label}>Depth</span>
                                                    <select className={input} value={selected.heartDepth || 'infront'} onChange={(e) => patchAsset(selectedAsset, { heartDepth: e.target.value })}>
                                                        <option value="infront">In front of heart</option>
                                                        <option value="behind">Behind heart</option>
                                                    </select>
                                                </label>
                                            ) : (
                                                <>
                                                    <label className="block">
                                                        <span className={label}>Anchor</span>
                                                        <select className={input} value={selected.anchor || 'top-left'} onChange={(e) => changeAnchor(selectedAsset, e.target.value)}>
                                                            {ANCHORS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                                                        </select>
                                                    </label>

                                                    {['top', 'bottom', 'left', 'right', 'x'].filter(k => selected.offsets?.[k] !== undefined).map(k => (
                                                        <label key={k} className="block">
                                                            <span className={label}>{k}</span>
                                                            <input
                                                                type="number"
                                                                className={input}
                                                                value={selected.offsets[k]}
                                                                onChange={(e) => patchAsset(selectedAsset, { offsets: { ...selected.offsets, [k]: Number(e.target.value) || 0 } })}
                                                            />
                                                        </label>
                                                    ))}
                                                </>
                                            )}
                                        </div>

                                        <div className="mt-2">
                                            <ImageSlot
                                                value={selected.image}
                                                onChange={(v) => patchAsset(selectedAsset, { image: v })}
                                                onPick={() => pickFile({ kind: 'asset', index: selectedAsset })}
                                                uploading={uploadingTo === `asset-${selectedAsset}`}
                                                label="/img/profileSkins/example.png"
                                            />
                                        </div>
                                    </div>
                                )}
                            </Panel>
                        </div>
                    </div>

                    {/* Inspector */}
                    <div className="flex flex-col gap-3 xl:col-span-5">
                        <Panel title={isCreating ? 'New skin' : form.name}>
                            <div className="grid grid-cols-2 gap-2">
                                <label className="block">
                                    <span className={label}>Name</span>
                                    <input className={input} value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="Summer 2026" />
                                </label>

                                <label className="block">
                                    <span className={label}>Skin ID</span>
                                    <input
                                        className={`${input} ${isCreating ? '' : 'bg-gray-100'}`}
                                        value={form.skinId}
                                        disabled={!isCreating}
                                        onChange={(e) => set({ skinId: e.target.value })}
                                        placeholder="summer26"
                                    />
                                </label>

                                <label className="block">
                                    <span className={label}>Sort order</span>
                                    <input type="number" className={input} value={form.sortOrder} onChange={(e) => set({ sortOrder: e.target.value })} />
                                </label>

                                <div className="flex items-end gap-3 pb-1">
                                    <label className="flex items-center gap-1 text-[13px] text-gray-700">
                                        <input type="checkbox" checked={!!form.isActive} onChange={(e) => set({ isActive: e.target.checked })} />
                                        Active
                                    </label>
                                    <label className="flex items-center gap-1 text-[13px] text-gray-700">
                                        <input type="checkbox" checked={!!form.staffOnly} onChange={(e) => set({ staffOnly: e.target.checked })} />
                                        Staff only
                                    </label>
                                </div>
                            </div>
                        </Panel>

                        <Panel title="Header artwork">
                            <div className="flex flex-col gap-2">
                                <ImageSlot
                                    value={form.backgroundImage}
                                    onChange={(v) => set({ backgroundImage: v })}
                                    onPick={() => pickFile({ kind: 'background' })}
                                    uploading={uploadingTo === 'background'}
                                    label="/img/profileSkins/summer26_bg.png"
                                />

                                <div className="grid grid-cols-2 gap-2">
                                    <label className="block">
                                        <span className={label}>Tint mode</span>
                                        <select className={input} value={form.backgroundMode} onChange={(e) => set({ backgroundMode: e.target.value })}>
                                            <option value="">Multiply with header colour</option>
                                            <option value="overlay">Overlay (ignore tint)</option>
                                        </select>
                                    </label>

                                    <label className="block">
                                        <span className={label}>Stagger offset</span>
                                        <input
                                            type="number"
                                            className={input}
                                            value={form.verticalOffset}
                                            onChange={(e) => set({ verticalOffset: e.target.value })}
                                        />
                                    </label>
                                </div>

                                <p className="text-[12px] text-gray-500">
                                    This image tiles across the box header at its natural size. Stagger offset shifts each tile column
                                    down by that many pixels for a diagonal effect.
                                </p>
                            </div>
                        </Panel>

                        <Panel title="Border">
                            <div className="flex flex-col gap-2">
                                <label className="block">
                                    <span className={label}>Style</span>
                                    <select className={input} value={gradientMode} onChange={(e) => setGradient(e.target.value, gradientValue)}>
                                        <option value="none">Solid colours</option>
                                        <option value="static">Gradient</option>
                                        <option value="animated">Animated gradient</option>
                                    </select>
                                </label>

                                {gradientMode === 'none' ? (
                                    <div className="flex flex-col gap-2">
                                        <div><span className={label}>Inner</span><Swatch value={form.borderInner} onChange={(v) => set({ borderInner: v })} title="Inner border" /></div>
                                        <div><span className={label}>Middle</span><Swatch value={form.borderMiddle} onChange={(v) => set({ borderMiddle: v })} title="Middle border" /></div>
                                        <div><span className={label}>Outer</span><Swatch value={form.borderOuter} onChange={(v) => set({ borderOuter: v })} title="Outer border" /></div>
                                    </div>
                                ) : (
                                    <div>
                                        <span className={label}>Gradient</span>
                                        <select
                                            className={input}
                                            value={gradientValue === 'rainbow' ? 'rainbow' : 'custom'}
                                            onChange={(e) => setGradient(gradientMode, e.target.value === 'rainbow' ? 'rainbow' : '#ffcc66')}
                                        >
                                            <option value="rainbow">Rainbow</option>
                                            <option value="custom">Single colour</option>
                                        </select>
                                        {gradientValue !== 'rainbow' && (
                                            <div className="mt-2">
                                                <Swatch value={gradientValue} onChange={(v) => setGradient(gradientMode, v)} title="Gradient colour" />
                                            </div>
                                        )}
                                    </div>
                                )}

                                <label className="block">
                                    <span className={label}>Corner radius</span>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="number"
                                            min={0}
                                            max={MAX_RADIUS}
                                            step={1}
                                            className={input}
                                            value={radiusToNumber(form.borderRadius)}
                                            onChange={(e) => set({ borderRadius: `${radiusToNumber(e.target.value)}px` })}
                                        />
                                        <span className="shrink-0 text-[12px] text-gray-500">px (max {MAX_RADIUS})</span>
                                    </div>
                                </label>
                            </div>
                        </Panel>

                        <Panel
                            title="Assets"
                            right={
                                <button type="button" onClick={() => pickFile({ kind: 'newAsset' })} className="font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-gold hover:underline">
                                    + Upload
                                </button>
                            }
                        >
                            {(form.assets || []).length === 0 ? (
                                <p className="text-[13px] text-gray-600">No assets yet. Upload one, or drop an image on the canvas.</p>
                            ) : (
                                <div className="flex flex-col gap-1">
                                    {form.assets.map((asset, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            onClick={() => setSelectedAsset(index)}
                                            className={`flex items-center gap-2 rounded px-2 py-1 text-left transition-all
                                                ${selectedAsset === index ? 'bg-maroon text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                                        >
                                            <span
                                                className="h-[30px] w-[30px] shrink-0 border border-black/20 bg-white"
                                                style={{ backgroundImage: `url(${resolveSkinAssetUrl(asset.image)})`, backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}
                                            />
                                            <span className="min-w-0 flex-1 truncate text-[12px]">{asset.image.split('/').pop()}</span>
                                            <span className="shrink-0 font-['Jersey_10'] text-[13px] uppercase tracking-[1px] opacity-70">
                                                {asset.placement || 'header'}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </Panel>

                        <Panel
                            title="Wallpapers"
                            right={
                                <button
                                    type="button"
                                    onClick={() => set({ presetBackgrounds: [...(form.presetBackgrounds || []), { image: '' }] })}
                                    className="font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-gold hover:underline"
                                >
                                    + Add
                                </button>
                            }
                        >
                            {(form.presetBackgrounds || []).length === 0 ? (
                                <p className="text-[13px] text-gray-600">None. These tile across the whole profile page and unlock with the skin.</p>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {form.presetBackgrounds.map((preset, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <ImageSlot
                                                value={preset.image}
                                                onChange={(v) => set({ presetBackgrounds: form.presetBackgrounds.map((p, i) => (i === index ? { image: v } : p)) })}
                                                onPick={() => pickFile({ kind: 'preset', index })}
                                                uploading={uploadingTo === 'preset'}
                                                label="/img/profileSkins/summer26_pbg.png"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setWallpaper(preset.image)}
                                                className="shrink-0 text-[12px] text-gray-600 hover:text-maroon"
                                            >
                                                preview
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => set({ presetBackgrounds: form.presetBackgrounds.filter((_, i) => i !== index) })}
                                                className="shrink-0 text-[12px] text-red-600 hover:underline"
                                            >
                                                remove
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Panel>

                        <div className="flex flex-wrap gap-2">
                            <button type="button" onClick={save} disabled={saving} className={`${btn} bg-maroon text-white disabled:opacity-60`} style={shadow}>
                                {saving ? 'Saving...' : isCreating ? 'Create skin' : dirty ? 'Save changes *' : 'Save changes'}
                            </button>
                            {!isCreating && (
                                <button type="button" onClick={remove} className={`${btn} bg-red-100 text-red-900 hover:bg-red-200`} style={shadow}>
                                    Delete
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => { setSelectedId(null); setForm(EMPTY_SKIN); setDirty(false); }}
                                className={`${btn} bg-gray-200 text-gray-800 hover:bg-gray-300`}
                                style={shadow}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProfileSkins;
